import { useLocation, useParams } from "react-router-dom";
import { lang } from "./language/Strings";
import { useMemo } from "react";

//Can we create a type that allows any number of nested routes of a base type?
export type RouteType = {
  __type?: "route"; // 👈 compiler-only discriminator
  url: (...args: any[]) => string;
  path: string;
  title: string;
};

export const routes = {
  Invoice: {
    Draft: {
      path: "/:companyId/invoice/draft",
      url: (companyId) => `/${companyId}/invoice/draft`,
      title: "Fakturaunderlag",
    } as RouteType,
  },
  VehicleInspection: {
    ActiveSchemas: {
      path: "/:companyId/form/schemas",
      url: (companyId) => `/${companyId}/form/schemas`,
      title: lang.vehicleInspection,
    } as RouteType,
    Form: {
      path: "/:companyId/form/:schemaId",
      url: (companyId, schemaId) => `/${companyId}/form/${schemaId}`,
      title: lang.vehicleInspection,
    } as RouteType,
    ShowFormResult: {
      path: "/:companyId/form/:schemaId/results/:formResultId",
      url: (companyId, schemaId, formResultId) =>
        `/${companyId}/form/${schemaId}/results/${formResultId}`,
      title: lang.vehicleInspection,
    } as RouteType,
  },
  Login: {
    url: () => "/Login",
    path: "/Login",
    title: lang.logIn,
  } as RouteType,
  Start: {
    url: (companyId) => `/${companyId}`,
    path: "/:companyId",
    title: lang.home,
  } as RouteType,
  SignInTest: {
    url: () => "/SignInTest",
    path: "/SignInTest",
    title: "SigninTest",
  } as RouteType,
  Redirect: {
    url: () => "/Login/Redirect",
    path: "/Login/Redirect",
    title: lang.redirecting,
  } as RouteType,
  RedirectToLogin: { url: () => "/", path: "/" } as RouteType,
  Reports: {
    url: (companyId) => `/${companyId}/reports`,
    path: "/:companyId/reports",
    title: lang.reports,
  } as RouteType,
  SigningSession: {
    Documents: {
      url: (companyId) => `/${companyId}/Documents`,
      path: "/:companyId/Documents",
      title: lang.documents,
    } as RouteType,
    DocumentById: {
      url: (companyId, documentId) => `/${companyId}/Documents/${documentId}`,
      path: "/:companyId/Documents/:documentId",
      title: lang.documents,
    } as RouteType,
    Sign: {
      url: (companyId, documentId, signingSessionId) =>
        `/${companyId}/Documents/${documentId}/Sign/${signingSessionId}`,
      path: "/:companyId/Documents/:documentId/Sign/:signingSessionId",
      title: lang.signDocument,
    } as RouteType,
  },
  Filter: {
    url: (companyId) => `/${companyId}/filter`,
    path: "/:companyId/filter",
    title: lang.filter,
  } as RouteType,
  Create: {
    url: (companyId) => `/${companyId}/inspection/Create`,
    path: "/:companyId/inspection/Create",
    title: lang.createInspection,
  } as RouteType,
  Company: {
    manage: {
      url: (companyId) => `${companyId}/company`,
      path: ":companyId/company",
      title: lang.company,
    } as RouteType,
  },
  Groups: {
    manage: {
      url: (companyId) => `/${companyId}/groups`,
      path: "/:companyId/groups",
      title: lang.groups,
    } as RouteType,
  },
  Planner: {
    path: "/:companyId/planner",
    url: (companyId) => `/${companyId}/planner`,
    title: lang.planner,
  } as RouteType,
  Users: {
    url: (companyId) => `/${companyId}/users`,
    path: "/:companyId/users",
    title: lang.users,
  } as RouteType,
  Assignments: {
    show: {
      url: (companyId, inspectionId) =>
        `/${companyId}/assignments/${inspectionId}`,
      path: "/:companyId/assignments/:inspectionId",
      title: lang.assignments,
    } as RouteType,
  },
  Report: {
    url: (companyId) => `/${companyId}/report`,
    path: "/:companyId/report",
    title: lang.report,
  } as RouteType,
  Deviation: {
    Pages: {
      List: {
        url: (companyId, deviationId?: string) =>
          deviationId
            ? `/${companyId}/deviation/${deviationId}/list`
            : `/${companyId}/deviation`,
        path: "/:companyId/deviation",
        title: lang.deviations,
      } as RouteType,
      Show: {
        url: (companyId, deviationId) =>
          `/${companyId}/deviation/${deviationId}`,
        path: "/:companyId/deviation/:deviationId",
        title: lang.deviations,
      } as RouteType,
      Create: {
        url: (companyId) => `/${companyId}/deviation/create`,
        path: "/:companyId/deviation/create",
        title: lang.createDeviation,
      } as RouteType,
      Edit: {
        url: (companyId, deviationId) =>
          `/${companyId}/deviation/${deviationId}/edit`,
        path: "/:companyId/deviation/:deviationId/edit",
        title: lang.edit,
      } as RouteType,
      Filter: {
        url: (companyId) => `/${companyId}/deviation/filter`,
        path: "/:companyId/deviation/filter",
        title: lang.filter,
      } as RouteType,
      Map: {
        url: (companyId, deviationId) =>
          deviationId
            ? `/${companyId}/deviation/${deviationId}/map`
            : `/${companyId}/deviation/map`,
        path: "/:companyId/deviation/map",
        title: lang.map,
      } as RouteType,
      MapByDeviationId: {
        url: (companyId, deviationId) =>
          `/${companyId}/deviation/${deviationId}/map`,
        path: "/:companyId/deviation/:deviationId/map",
        title: lang.map,
      } as RouteType,
    },
  },
  Map: {
    url: (companyId: string, inspectionId?: string | undefined) =>
      inspectionId
        ? `/${companyId}/inspection/${inspectionId}/map`
        : `/${companyId}/inspection/map`,
    path: "/:companyId/inspection/map",
    title: lang.map,
  } as RouteType,
  MapByInspectionId: {
    url: (companyId, inspectionId) =>
      `/${companyId}/inspection/${inspectionId}/map`,
    path: "/:companyId/inspection/:inspectionId/map",
    title: lang.map,
  } as RouteType,
  Egenkontroller: {
    Pages: {
      List: {
        url: (companyId, inspectionId?: string) =>
          inspectionId
            ? `/${companyId}/inspection/${inspectionId}/list`
            : `/${companyId}/inspection`,
        path: "/:companyId/inspection",
        title: lang.egenkontrollerTitle,
      } as RouteType,
      ListWithInspectionId: {
        url: (companyId, inspectionId) =>
          `/${companyId}/inspection/${inspectionId}/list`,
        path: "/:companyId/inspection/:inspectionId/list",
        title: lang.egenkontrollerTitle,
      } as RouteType,
      Show: {
        url: (companyId, inspectionId) =>
          `/${companyId}/inspection/${inspectionId}`,
        path: "/:companyId/inspection/:inspectionId",
        title: lang.egenkontrollerTitle,
      } as RouteType,
      Create: {
        url: (companyId) => `/${companyId}/inspection/create`,
        path: "/:companyId/inspection/create",
        title: lang.createInspection,
      } as RouteType,
      Edit: {
        url: (companyId, inspectionId) =>
          `/${companyId}/inspection/${inspectionId}/edit`,
        path: "/:companyId/inspection/:inspectionId/edit",
        title: lang.editSelfInspection,
      } as RouteType,
      Filter: {
        url: (companyId) => `/${companyId}/inspection/filter`,
        path: "/:companyId/inspection/filter",
        title: lang.filter,
      } as RouteType,
      Map: {
        url: (companyId) => `/${companyId}/inspection/map`,
        path: "/:companyId/inspection/map",
        title: lang.map,
      } as RouteType,
      MapByInspectionId: {
        url: (companyId, inspectionId) =>
          `/${companyId}/inspection/${inspectionId}/map`,
        path: "/:companyId/inspection/:inspectionId/map",
        title: lang.map,
      } as RouteType,
      MapByDeviationId: {
        url: (companyId, inspectionId, deviationId) =>
          `/${companyId}/inspection/${inspectionId}/deviation/${deviationId}/map`,
        path: "/:companyId/inspection/:inspectionId/deviation/:deviationId/map",
        title: lang.map,
      } as RouteType,
      Deviations: {
        List: {
          url: (companyId, inspectionId) =>
            `/${companyId}/inspection/${inspectionId}/deviations`,
          path: "/:companyId/inspection/:inspectionId/deviations",
          title: lang.deviations,
        } as RouteType,

        ShowById: {
          url: (companyId, inspectionId, deviationId) =>
            `/${companyId}/inspection/${inspectionId}/deviations/${deviationId}`,
          path: "/:companyId/inspection/:inspectionId/deviations/:deviationId",
          title: lang.deviations,
        } as RouteType,
        Edit: {
          url: (companyId, inspectionId, deviationId) =>
            `/${companyId}/inspection/${inspectionId}/deviations/${deviationId}/edit`,
          path: "/:companyId/inspection/:inspectionId/deviations/:deviationId/edit",
          title: lang.edit,
        } as RouteType,
        Create: {
          url: (companyId, inspectionId) =>
            `/${companyId}/inspection/${inspectionId}/deviations/create`,
          path: "/:companyId/inspection/:inspectionId/deviations/create",
          title: lang.createDeviation,
        } as RouteType,
      },
    },
    Deviation: {
      Show: {
        url: (companyId, inspectionId) =>
          `/${companyId}/inspection/${inspectionId}/deviation`,
        path: "/:companyId/inspection/:inspectionId/deviation",
        title: lang.deviations,
      } as RouteType,
      ShowById: {
        url: (companyId, inspectionId, deviationId) =>
          `/${companyId}/inspection/${inspectionId}/deviation/${deviationId}/show`,
        path: "/:companyId/inspection/:inspectionId/deviation/:deviationId/show",
        title: lang.deviations,
      } as RouteType,
      Edit: {
        url: (companyId, inspectionId, deviationId) =>
          `/${companyId}/inspection/${inspectionId}/deviation/${deviationId}/edit`,
        path: "/:companyId/inspection/:inspectionId/deviation/:deviationId/edit",
        title: lang.edit,
      } as RouteType,
      Create: {
        url: (companyId, inspectionId) =>
          `/${companyId}/inspection/${inspectionId}/deviation/create`,
        path: "/:companyId/inspection/:inspectionId/deviation/create",
        title: lang.createDeviation,
      } as RouteType,
    },
  },

  SignOut: {
    url: () => `/Login/SignOut`,
    path: "/Login/SignOut",
    title: lang.signOut,
  } as RouteType,
};

function comparePathNameOfArray(
  path: string,
  paths: RouteType[],
  params: Record<string, string>
) {
  return paths.some((route) => {
    const routePath = route.path.replace(
      /:(\w+)/g,
      (match, param) => params[param] || match
    );
    return routePath.toLowerCase() === path.toLowerCase();
  });
}

function getCurrentRoute(
  path: string,
  entry: any,
  params: Record<string, string | undefined>
) {
  if (entry.path) {
    const routePath = entry.path.replace(
      /:(\w+)/g,
      (match, param) => params[param] || match
    );

    return routePath.toLowerCase() === path.toLowerCase() ? entry : null;
  } else {
    for (const value of Object.values(entry)) {
      const match = getCurrentRoute(path, value, params);
      if (match) return match;
    }
    return null;
  }
}

export function useRoute() {
  const location = useLocation();
  const params = useParams();
  const route = useMemo(() => {
    for (const val of Object.values(routes)) {
      const match = getCurrentRoute(location.pathname, val, params);
      if (match) return match as RouteType;
    }
    return null;
  }, [location.pathname, params]);
  return route as RouteType;
}
